/**
 * CNC PM PWA - Cloud Sync Queue Engine & Auto-Sync State Machine
 * Coordinates offline queuing, auto-sync on network reconnect, exponential backoff retries,
 * and draining of both Google Sheets and LINE webhook queue entries.
 * Per PROJECT.md § Architecture, Feature F5.2 & ORIGINAL_REQUEST.md § R5, R6.
 */

import type {
  InspectionRecord,
  SyncQueueEntry,
  QueueSyncStatus,
  GasSyncPayload,
} from '../../types';
import {
  db,
  getSetting,
  updateInspectionSyncStatus,
} from '../../db';
import {
  sendBatchInspections,
  sendSingleInspection,
  validateGoogleWebAppUrl,
} from './googleSheetsClient';

export interface DrainQueueResult {
  processed: number;
  succeeded: number;
  failed: number;
  errors: string[];
}

export type QueueUpdateListener = (pendingCount: number) => void;

class SyncQueueEngine {
  private isProcessing = false;
  private listenersInitialized = false;
  private periodicIntervalId: any = null;
  private onlineHandler: (() => void) | null = null;
  private visibilityHandler: (() => void) | null = null;
  private updateSubscribers: Set<QueueUpdateListener> = new Set();

  /**
   * Subscribe to queue count/state changes for UI reactivity
   */
  public subscribe(listener: QueueUpdateListener): () => void {
    this.updateSubscribers.add(listener);
    // Immediately notify subscriber with current count
    this.getPendingCount().then((count) => listener(count)).catch(() => {});
    return () => {
      this.updateSubscribers.delete(listener);
    };
  }

  private notifySubscribers(count: number): void {
    for (const listener of this.updateSubscribers) {
      try {
        listener(count);
      } catch (err) {
        console.error('[SyncQueueEngine] Subscriber callback error:', err);
      }
    }
  }

  /**
   * Initialize browser network event listeners and periodic sweep
   */
  public initSyncListeners(): void {
    if (this.listenersInitialized) return;
    if (typeof window === 'undefined') return;

    this.onlineHandler = () => {
      console.log('[SyncQueueEngine] Network restored (online event). Triggering auto-sync drain.');
      this.drainQueue().catch((err) => {
        console.error('[SyncQueueEngine] Auto-sync error on online event:', err);
      });
    };

    this.visibilityHandler = () => {
      if (document.visibilityState === 'visible' && navigator.onLine) {
        this.drainQueue().catch((err) => {
          console.error('[SyncQueueEngine] Auto-sync error on visibility change:', err);
        });
      }
    };

    window.addEventListener('online', this.onlineHandler);
    document.addEventListener('visibilitychange', this.visibilityHandler);

    // Periodic sweep every 60 seconds
    this.periodicIntervalId = setInterval(() => {
      if (typeof navigator !== 'undefined' && navigator.onLine) {
        this.drainQueue().catch((err) => {
          console.error('[SyncQueueEngine] Auto-sync periodic sweep error:', err);
        });
      }
    }, 60000);

    this.listenersInitialized = true;
  }

  /**
   * Clean up registered listeners (useful for testing or teardown)
   */
  public destroySyncListeners(): void {
    if (typeof window !== 'undefined') {
      if (this.onlineHandler) window.removeEventListener('online', this.onlineHandler);
      if (this.visibilityHandler) document.removeEventListener('visibilitychange', this.visibilityHandler);
    }
    if (this.periodicIntervalId) {
      clearInterval(this.periodicIntervalId);
      this.periodicIntervalId = null;
    }
    this.listenersInitialized = false;
  }

  /**
   * Calculate exponential backoff delay with random jitter
   */
  public calculateBackoffDelayMs(attempts: number): number {
    const baseMs = 3000; // 3s
    const maxMs = 60000; // 60s
    const exponential = baseMs * Math.pow(2, attempts);
    const delay = Math.min(maxMs, exponential);
    const jitter = Math.floor(Math.random() * 1500); // 0-1.5s jitter
    return delay + jitter;
  }

  /**
   * Get total count of items awaiting sync or retry
   */
  public async getPendingCount(): Promise<number> {
    try {
      return await db.syncQueue
        .where('status')
        .anyOf('PENDING', 'FAILED', 'SYNCING' as any)
        .count();
    } catch {
      return 0;
    }
  }

  /**
   * Enqueue an inspection record for Google Sheets sync
   */
  public async enqueueInspection(record: InspectionRecord): Promise<number> {
    const gasUrl = (await getSetting<string>('gas_endpoint_url')) || '';
    const now = new Date().toISOString();

    const entryId = await db.syncQueue.add({
      queueId: `sync_${record.id}_${Date.now()}`,
      recordId: record.id,
      endpointType: 'google_sheets',
      targetUrl: gasUrl,
      payload: {
        action: 'single_inspection',
        inspections: [record],
        timestamp: now,
      } as GasSyncPayload,
      attempts: 0,
      maxAttempts: 5,
      status: 'PENDING',
      createdAt: now,
    });

    const pendingCount = await this.getPendingCount();
    this.notifySubscribers(pendingCount);

    // If online, attempt immediate sync
    if (typeof navigator !== 'undefined' && navigator.onLine) {
      setTimeout(() => this.drainQueue().catch(() => {}), 100);
    }

    return entryId;
  }

  /**
   * Drain the entire offline queue with FIFO ordering and batching
   */
  public async drainQueue(): Promise<DrainQueueResult> {
    if (this.isProcessing) {
      console.log('[SyncQueueEngine] Queue drain already in progress, skipping duplicate run.');
      return { processed: 0, succeeded: 0, failed: 0, errors: [] };
    }

    if (typeof navigator !== 'undefined' && typeof navigator.onLine === 'boolean' && !navigator.onLine) {
      console.log('[SyncQueueEngine] Device currently offline; skipping queue drain.');
      return { processed: 0, succeeded: 0, failed: 0, errors: ['Device offline'] };
    }

    this.isProcessing = true;
    const result: DrainQueueResult = {
      processed: 0,
      succeeded: 0,
      failed: 0,
      errors: [],
    };

    try {
      // 1. Drain Google Sheets queue entries
      await this.drainGoogleSheetsEntries(result);

      // 2. Drain LINE Webhook queue entries
      await this.drainLineWebhookEntries(result);

      // 3. Fallback check: Drain any un-queued inspection records still marked PENDING_SYNC
      await this.drainOrphanedPendingInspections(result);
    } finally {
      this.isProcessing = false;
      const count = await this.getPendingCount();
      this.notifySubscribers(count);
    }

    return result;
  }

  /**
   * Process all pending Google Sheets sync entries
   */
  private async drainGoogleSheetsEntries(result: DrainQueueResult): Promise<void> {
    // Check if sync is enabled in settings
    const gasConfig = await getSetting<{ enabled?: boolean; webAppUrl?: string }>('gas_config');
    if (gasConfig && gasConfig.enabled === false) {
      console.log('[SyncQueueEngine] Google Sheets sync is disabled in settings; skipping dispatch.');
      return;
    }

    const defaultUrl = (await getSetting<string>('gas_endpoint_url')) || gasConfig?.webAppUrl || '';

    // Query pending or retrying items in FIFO order
    const pendingItems = await db.syncQueue
      .where('status')
      .anyOf('PENDING', 'FAILED')
      .toArray();

    const sheetsItems = pendingItems
      .filter((item) => item.endpointType === 'google_sheets')
      .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

    if (sheetsItems.length === 0) return;

    // Filter items whose nextRetryAt has arrived
    const nowMs = Date.now();
    const readyItems = sheetsItems.filter((item) => {
      if (!item.nextRetryAt) return true;
      return new Date(item.nextRetryAt).getTime() <= nowMs;
    });

    if (readyItems.length === 0) return;

    // Group items by target URL
    const itemsByUrl = new Map<string, SyncQueueEntry[]>();
    for (const item of readyItems) {
      const url = item.targetUrl || defaultUrl;
      if (!url) {
        console.warn(`[SyncQueueEngine] Queue item ${item.queueId} has no target URL and no default URL configured.`);
        continue;
      }
      if (!itemsByUrl.has(url)) {
        itemsByUrl.set(url, []);
      }
      itemsByUrl.get(url)!.push(item);
    }

    for (const [targetUrl, urlItems] of itemsByUrl.entries()) {
      const urlValidation = validateGoogleWebAppUrl(targetUrl);
      if (!urlValidation.valid) {
        console.error(`[SyncQueueEngine] Invalid target URL for batch: ${targetUrl}`);
        for (const item of urlItems) {
          if (item.id) {
            await this.markItemFailed(item, urlValidation.error || 'Invalid URL');
            result.failed++;
          }
        }
        continue;
      }

      // Collect inspection records for batch processing (batches of up to 25)
      const batchSize = 25;
      for (let i = 0; i < urlItems.length; i += batchSize) {
        const chunk = urlItems.slice(i, i + batchSize);

        // Mark items as SYNCING
        for (const item of chunk) {
          if (item.id) {
            await db.syncQueue.update(item.id, { status: 'SYNCING' as QueueSyncStatus });
          }
        }

        // Gather all InspectionRecord objects
        const recordsToSync: InspectionRecord[] = [];
        const validChunkItems: SyncQueueEntry[] = [];

        for (const item of chunk) {
          result.processed++;
          try {
            let record: InspectionRecord | undefined;

            // Check if payload already has the inspection record
            if (item.payload?.inspections && Array.isArray(item.payload.inspections) && item.payload.inspections[0]) {
              record = item.payload.inspections[0];
            } else if (item.recordId) {
              record = await db.inspections.get(item.recordId);
            }

            if (record) {
              recordsToSync.push(record);
              validChunkItems.push(item);
            } else {
              // Isolated corrupted item without valid record
              console.warn(`[SyncQueueEngine] Record not found for queue item ${item.queueId}, marking failed`);
              if (item.id) {
                await this.markItemFailed(item, 'Inspection record not found in local database');
                result.failed++;
              }
            }
          } catch (corruptErr: any) {
            console.error(`[SyncQueueEngine] Error reading queue item ${item.queueId}:`, corruptErr);
            if (item.id) {
              await this.markItemFailed(item, corruptErr.message);
              result.failed++;
            }
          }
        }

        if (recordsToSync.length === 0) continue;

        try {
          // Send batch to Google Sheets Web App
          const syncResponse = recordsToSync.length === 1
            ? await sendSingleInspection(targetUrl, recordsToSync[0])
            : await sendBatchInspections(targetUrl, recordsToSync);

          if (syncResponse.success) {
            const nowIso = new Date().toISOString();

            // Mark all items in chunk as SUCCESS
            for (const item of validChunkItems) {
              if (item.id) {
                await db.syncQueue.update(item.id, {
                  status: 'SUCCESS',
                  completedAt: nowIso,
                  lastError: null,
                });
              }

              // Update inspection record sync status
              if (item.recordId) {
                await updateInspectionSyncStatus(item.recordId, 'SYNCED', nowIso);
              }
              result.succeeded++;
            }
          } else {
            throw new Error(syncResponse.message || 'Google Sheets sync rejected payload');
          }
        } catch (syncErr: any) {
          const errMsg = syncErr.message || 'Sync error';
          result.errors.push(errMsg);
          console.warn(`[SyncQueueEngine] Batch sync failed: ${errMsg}`);

          for (const item of validChunkItems) {
            if (item.id) {
              await this.handleItemSyncFailure(item, errMsg);
              result.failed++;
            }
          }
        }
      }
    }
  }

  /**
   * Process all pending LINE Webhook alert entries
   */
  private async drainLineWebhookEntries(result: DrainQueueResult): Promise<void> {
    const pendingItems = await db.syncQueue
      .where('status')
      .anyOf('PENDING', 'FAILED')
      .toArray();

    const lineItems = pendingItems
      .filter((item) => item.endpointType === 'line_webhook')
      .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

    if (lineItems.length === 0) return;

    const nowMs = Date.now();
    for (const item of lineItems) {
      if (item.nextRetryAt && new Date(item.nextRetryAt).getTime() > nowMs) {
        continue;
      }

      result.processed++;
      if (item.id) {
        await db.syncQueue.update(item.id, { status: 'SYNCING' as QueueSyncStatus });
      }

      try {
        if (!item.targetUrl) {
          throw new Error('Missing target webhook URL for LINE alert');
        }

        const response = await fetch(item.targetUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json; charset=UTF-8',
          },
          body: JSON.stringify(item.payload),
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const nowIso = new Date().toISOString();
        if (item.id) {
          await db.syncQueue.update(item.id, {
            status: 'SUCCESS',
            completedAt: nowIso,
            lastError: null,
          });
        }

        if (item.recordId) {
          await db.inspections.update(item.recordId, {
            alertDispatched: true,
            updatedAt: nowIso,
          });
        }
        result.succeeded++;
      } catch (lineErr: any) {
        const errMsg = lineErr.message || 'LINE alert dispatch failed';
        result.errors.push(errMsg);
        if (item.id) {
          await this.handleItemSyncFailure(item, errMsg);
          result.failed++;
        }
      }
    }
  }

  /**
   * Sweep for any inspection records marked PENDING_SYNC that haven't been queued
   */
  private async drainOrphanedPendingInspections(result: DrainQueueResult): Promise<void> {
    try {
      const pendingInspections = await db.inspections
        .where('syncStatus')
        .equals('PENDING_SYNC')
        .toArray();

      if (pendingInspections.length === 0) return;

      const gasUrl = (await getSetting<string>('gas_endpoint_url')) || '';
      if (!gasUrl) return;

      for (const ins of pendingInspections) {
        // Check if already in queue
        const existing = await db.syncQueue
          .where('recordId')
          .equals(ins.id)
          .first();

        if (!existing) {
          // Enqueue it now
          await this.enqueueInspection(ins);
        }
      }
    } catch (err) {
      console.warn('[SyncQueueEngine] Error checking orphaned pending inspections:', err);
    }
  }

  /**
   * Handle retry calculation and status update upon a failed transmission
   */
  private async handleItemSyncFailure(item: SyncQueueEntry, errorMsg: string): Promise<void> {
    if (!item.id) return;

    const nextAttempts = (item.attempts || 0) + 1;
    const maxAttempts = item.maxAttempts || 5;

    if (nextAttempts >= maxAttempts) {
      // Mark permanently failed
      await this.markItemFailed(item, errorMsg);
      if (item.recordId) {
        await db.inspections.update(item.recordId, {
          syncStatus: 'FAILED',
          updatedAt: new Date().toISOString(),
        });
      }
    } else {
      // Calculate exponential backoff
      const delayMs = this.calculateBackoffDelayMs(nextAttempts);
      const nextRetryIso = new Date(Date.now() + delayMs).toISOString();

      await db.syncQueue.update(item.id, {
        attempts: nextAttempts,
        status: 'FAILED', // Or FAILED_RETRY
        lastError: errorMsg,
        nextRetryAt: nextRetryIso,
      });
    }
  }

  /**
   * Mark an item as permanently failed
   */
  private async markItemFailed(item: SyncQueueEntry, errorMsg: string): Promise<void> {
    if (!item.id) return;
    await db.syncQueue.update(item.id, {
      status: 'FAILED',
      lastError: errorMsg,
      nextRetryAt: undefined,
    });
  }

  /**
   * Clear completed entries from the sync queue
   */
  public async clearCompleted(): Promise<void> {
    await db.syncQueue.where('status').equals('SUCCESS').delete();
    const count = await this.getPendingCount();
    this.notifySubscribers(count);
  }

  /**
   * Clear all items from the sync queue
   */
  public async clearAll(): Promise<void> {
    await db.syncQueue.clear();
    this.notifySubscribers(0);
  }
}

export const syncQueueEngine = new SyncQueueEngine();

// Auto-initialize browser listeners when loaded in browser runtime
if (typeof window !== 'undefined') {
  syncQueueEngine.initSyncListeners();
}
